import React, { useState, useEffect } from "react";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";
import { useUser } from "../config/userProvider";

const ApplicationsSheet = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    status: [],
    reference: "",
    interviewBy: "",
    trainer: "",
    startDate: "",
    endDate: "",
    sortBy: "createdAt", // ✅ new
    sortOrder: "desc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(2000);
  const [totalItems, setTotalItems] = useState(0);

  const { userData } = useUser();

  const capitalize = (str) =>
    str
      ? str
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ")
      : "";

  const getReferenceDisplay = (emp) => {
    const refType = emp.referenceDetails?.referenceType;
    if (refType === "employee")
      return `EMP-${capitalize(emp.referenceDetails?.employeeName || "")}`;
    if (refType === "recruiter")
      return `HR-${capitalize(emp.referenceDetails?.recruiterName || "")}`;
    if (refType === "consultancy")
      return emp.referenceDetails?.consultancyReference
        ? capitalize(emp.referenceDetails.consultancyReference)
        : "CONSULTANCY";
    if (refType === "other")
      return `OTHER-${capitalize(emp.referenceDetails?.otherReference || "")}`;
    if (refType === "walkin") return "WALKIN";
    return "N/A";
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "";

  const normalizeStatus = (status) => {
    if (!status) return "";
    const s = status.toLowerCase();
    if (s === "hold") return "HOLD";
    if (s === "not-started") return "NOT-STARTED";
    if (s === "in-progress") return "IN-PROGRESS";
    if (["pass", "certified", "completed"].includes(s)) return "CERTIFIED";
    if (["fail", "failed", "not-certified"].includes(s)) return "NOT-CERTIFIED";
    return s.toUpperCase();
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const today = new Date().toISOString().split("T")[0];
      const startDate = filters.startDate || today;
      const endDate = filters.endDate || today;

      params.append("startDate", startDate);
      params.append("endDate", endDate);

      if (filters.search) params.append("search", filters.search);
      if (filters.status.length)
        filters.status.forEach((s) => params.append("applicationStatus", s));
      if (filters.reference) params.append("referenceType", filters.reference);
      if (filters.interviewBy)
        params.append("interviewBy", filters.interviewBy);
      if (filters.trainer) params.append("trainer", filters.trainer);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      const res = await fetch(
        `${apiurl}/api/application/applications-sheet?${params.toString()}`
      );
      const data = await res.json();

      // ONLY applications (exclude employees without applications)
      //   setApplications(data?.applications?.filter((a) => a.type === "application") || []);
      setApplications(data?.applications || []);
      setTotalItems(data?.pagination?.totalItems || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const today = new Date().toISOString().split("T")[0];

    setFilters((prev) => ({
      ...prev,
      startDate: urlParams.get("startDate") || today,
      endDate: urlParams.get("endDate") || today,
      search: urlParams.get("search") || "",
    }));
  }, []);

  const maskPhone = (phone) => {
    if (!phone) return "";
    const strPhone = phone.toString();
    return strPhone.length > 4
      ? "*".repeat(strPhone.length - 4) + strPhone.slice(-4)
      : strPhone;
  };

  // ✅ Mask email → show only alternate characters before @
  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email || "";
    const [local, domain] = email.split("@");
    const maskedLocal = local
      .split("")
      .map((ch, idx) => (idx % 2 === 0 ? ch : "*"))
      .join("");
    return `${maskedLocal}@${domain}`;
  };

//   useEffect(() => {
//     if (!filters.startDate || !filters.endDate) return;
//     fetchApplications();
//   }, [
//     filters.startDate,
//     filters.endDate,
//     filters.search,
//     currentPage,
//     itemsPerPage,
//   ]);
useEffect(() => {
  if (!filters.startDate || !filters.endDate) return;
  fetchApplications();
}, [
  filters.startDate,
  filters.endDate,
  filters.search,
  filters.sortBy,       // ✅ added
  filters.sortOrder,    // ✅ added
  currentPage,
  itemsPerPage,
]);


  const exportToExcel = () => {
    if (!applications.length) return alert("No data to export");

    const flatten = (app, index) => ({
      "S.NO": index + 1,
      "APPLICATION ID": app.id || "",
      DATE: formatDate(app.createdAt),
      "EMPLOYEE ID": app.employeeId || "",
      NAME: capitalize(
        `${app.candidateSnapshot?.firstName || ""} ${
          app.candidateSnapshot?.lastName || ""
        }`
      ),
      PHONE: maskPhone(app.candidateSnapshot?.phone || ""), // ✅
      EMAIL: maskEmail(app.candidateSnapshot?.email || ""), // ✅
      PROFILE: app.positionTitle || "",
      REFERENCE: getReferenceDisplay(app),
      "APPLICATION STATUS": (app.applicationStatus || "").toUpperCase(),
      "INTERVIEW BY": capitalize(app.screeningDetails?.screenerName || ""),
      "TRAINING BY": capitalize(app.trainingDetails?.trainerName || ""),
      "OFFERED SALARY": app.offerDetails?.offeredCTC || "",
      DOJ: formatDate(app.offerDetails?.joiningDate),
      "MOCK CALL": normalizeStatus(
        app.trainingDetails?.mockcall?.[app.trainingDetails.mockcall.length - 1]
          ?.status
      ),
      OJT: normalizeStatus(app.trainingDetails?.ojt?.status),
      "EMPLOYEE MONTHLY SALARY":
        app.employeeDetails?.offerDetails?.salaryDetails?.monthlySalary || "",
      "EMPLOYEE STATUS": app.employeeDetails?.employeeStatus || "",
    });

    const worksheet = XLSX.utils.json_to_sheet(applications.map(flatten));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "APPLICATIONS");
    XLSX.writeFile(
      workbook,
      `APPLICATIONS_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">
            APPLICATIONS SHEET ({totalItems})
          </h1>
          <div className="flex gap-2">
            <select
              className="border rounded px-2 py-1"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, sortBy: e.target.value }))
              }
            >
              <option value="createdAt">Date</option>
              <option value="employeeId">Employee ID</option>
              <option value="applicationStatus">Status</option>
              <option value="positionTitle">Profile</option>
            </select>

            <select
              className="border rounded px-2 py-1"
              value={filters.sortOrder}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, sortOrder: e.target.value }))
              }
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>

            <select
              className="border rounded px-2 py-1"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              {[2000, 3000, 5000].map((num) => (
                <option key={num} value={num}>
                  {num} per page
                </option>
              ))}
            </select>

            {userData?.permissions?.some(
              (p) => p.resource === "download_applications"
            ) && (
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                <FaFileExcel /> Download Excel ({applications.length})
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <SuwasthiLoader />
        ) : applications.length === 0 ? (
          <div className="text-center p-4 bg-white rounded shadow text-sm">
            No applications found.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow p-2 text-xs">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-200">
                  {[
                    "S.NO",
                    "APPLICATION ID",
                    "DATE",
                    "EMPLOYEE ID",
                    "NAME",
                    "PHONE",
                    "EMAIL",
                    "PROFILE",
                    "REFERENCE",
                    "APPLICATION STATUS",
                    "INTERVIEW BY",
                    "TRAINING BY",
                    "OFFERED SALARY",
                    "DOJ",
                    "MOCK CALL",
                    "OJT",
                    "EMPLOYEE MONTHLY SALARY",
                    "EMPLOYEE STATUS",
                  ].map((col) => (
                    <th key={col} className="p-1 text-left">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {applications.map((app, index) => (
                  <tr key={app._id} className="border-b hover:bg-gray-50">
                    <td className="p-1">{index + 1}</td>
                    <td className="p-1">{app.id}</td>
                    <td className="p-1">{formatDate(app.createdAt)}</td>
                    <td className="p-1">{app.employeeId || "--"}</td>
                    <td className="p-1">
                      {capitalize(
                        `${app.candidateSnapshot?.firstName || ""} ${
                          app.candidateSnapshot?.lastName || ""
                        }`
                      )}
                    </td>
                    <td className="p-1">
                      {maskPhone(app.candidateSnapshot?.phone || "")}
                    </td>
                    <td className="p-1">
                      {maskEmail(app.candidateSnapshot?.email || "")}
                    </td>
                    <td className="p-1">{app.positionTitle || ""}</td>
                    <td className="p-1">{getReferenceDisplay(app)}</td>
                    <td className="p-1">
                      {(app.applicationStatus || "").toUpperCase()}
                    </td>
                    <td className="p-1">
                      {capitalize(app.screeningDetails?.screenerName || "")}
                    </td>
                    <td className="p-1">
                      {capitalize(app.trainingDetails?.trainerName || "")}
                    </td>
                    <td className="p-1">
                      {app.offerDetails?.offeredCTC || ""}
                    </td>
                    <td className="p-1">
                      {formatDate(app.offerDetails?.joiningDate)}
                    </td>
                    <td className="p-1">
                      {normalizeStatus(
                        app.trainingDetails?.mockcall?.[
                          app.trainingDetails.mockcall.length - 1
                        ]?.status
                      )}
                    </td>
                    <td className="p-1">
                      {normalizeStatus(app.trainingDetails?.ojt?.status)}
                    </td>
                    <td className="p-1">
                      {app.employeeDetails?.offerDetails?.salaryDetails
                        ?.monthlySalary || ""}
                    </td>
                    <td className="p-1">
                      {app.employeeDetails?.employeeStatus || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </Container>
    </Layout>
  );
};

export default ApplicationsSheet;
